import React, { useMemo, useState } from 'react'
import type { DndContext as DndKitDndContext } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import type { SortableContext } from './TableSortableUI'

interface ListItem {
  id: string
}

export const useTableSortable = <T extends ListItem>({
  list,
  onPositionChange,
}: {
  list: T[]
  onPositionChange: (oldIndex: number, newIndex: number) => void
}) => {
  const [activeId, setActiveId] = useState<string>('')

  const [listItemsOrder, setListItemsOrder] = React.useState<string[]>([])

  const listItemsOrdered = React.useMemo(() => {
    if (!listItemsOrder.length) {
      return list
    }

    return [...list].sort((a, b) => {
      const aIndex = listItemsOrder.indexOf(a.id)
      const bIndex = listItemsOrder.indexOf(b.id)

      // if there is no index, it means that list should be sorted by default
      if (aIndex === -1 || bIndex === -1) {
        return 0
      }

      return aIndex - bIndex
    })
  }, [list, listItemsOrder])

  const items = useMemo(() => listItemsOrdered?.map(({ id }) => id), [listItemsOrdered])

  const dndContextProps: React.ComponentProps<typeof DndKitDndContext> = {
    onDragStart: ({ active }) => {
      setActiveId(active.id as string)
    },
    onDragEnd: ({ active, over }) => {
      if (over && active.id !== over?.id) {
        setListItemsOrder(() => {
          const oldIndex = items.indexOf(active.id as string)
          const newIndex = items.indexOf(over.id as string)

          onPositionChange(oldIndex, newIndex)

          return arrayMove(items, oldIndex, newIndex)
        })
      }
      setActiveId('')
    },
    onDragCancel: () => {
      setActiveId('')
    },
  }

  const sortableContextProps: Omit<React.ComponentProps<typeof SortableContext>, 'children'> = {
    items,
  }

  return {
    activeId,
    listItemsOrdered,
    dndContextProps,
    sortableContextProps,
  }
}
